import {
  BrandKit,
  BrandKitAsset,
  MediaAsset,
  StoryboardSection,
} from "pages/media/types";
import { countScriptWords } from "pages/media/helpers/scriptAnchors";
import {
  DESKTOP_ASPECT_RATIO,
  MOBILE_ASPECT_RATIO,
  SPEECH_WORDS_PER_MINUTE,
  TABLET_ASPECT_RATIO,
} from "pages/media/constants";
import { timePretty } from "../../storyboard/editor/helpers";
import {
  DEFAULT_VIDEO_RELATIVE_HEIGHT,
  DIMENSION_PRECISION,
  SCENE_RELATIVE_HEIGHT_MOBILE_TABLET,
  SCENE_RELATIVE_WIDTH_DESKTOP,
} from "../constants";
import {
  CustomText,
  Logo,
  MainImage,
  MediaItem,
  ParameteredText,
  Position,
  ResizeHandlePosition,
  Shape,
  Size,
  Stroke,
  Table,
  TemplateState,
  VideoScene,
} from "../types";
import { TemplateItem } from "types/";
import { roundToHalfPixel } from "./itemsHelper";
import { isEqual } from "lodash";
import { CSSProperties } from "react";
import { useBrandKit } from "hooks/useBrandKit/";

/**
 * Per-chapter `include_transition` is the source of truth. The project-level
 * `hasTransition` is read only as a fallback for chapters that have no
 * media-settings row yet — typically projects created before the per-chapter
 * toggle existed.
 */
export function getChapterHasTransition(
  chapterSettings: { include_transition?: boolean | null } | undefined,
  hasTransition: boolean
): boolean {
  return chapterSettings?.include_transition ?? hasTransition;
}

export function getIntroEnabled(
  mediaGenerationSettings: { enable_intro?: boolean | null } | undefined,
  brandKitHasIntro: boolean
): boolean {
  return mediaGenerationSettings?.enable_intro ?? brandKitHasIntro;
}

export function getClosingEnabled(
  mediaGenerationSettings: { enable_closing?: boolean | null } | undefined,
  brandKitHasClosing: boolean
): boolean {
  return mediaGenerationSettings?.enable_closing ?? brandKitHasClosing;
}

export const getNumberRoundedToPrecision = (
  number: number,
  precision = DIMENSION_PRECISION
) => {
  if (!number) return 0;
  return Number.parseFloat(number.toFixed(precision));
};

export const getFirstSentence = (text: string) => {
  if (!text) return "";

  const MAX_WORDS = 12;
  const words = text.split(/\s+/).filter(Boolean);

  if (words.length <= MAX_WORDS) return text;
  return words.slice(0, MAX_WORDS).join(" ");
};

export const getRowKey = (
  asset: BrandKitAsset | Partial<MediaAsset> | Logo
) => {
  if ("asset_id" in asset) return asset.asset_id;
  if ("id" in asset) return asset.id;
  if ("assetId" in asset) return asset.assetId ?? undefined;
  else return;
};
export const getRowLabel = (font: BrandKitAsset | Partial<MediaAsset>) => {
  if ("name" in font) return font.name;
  if ("description" in font) return font.description ?? "";
  return "";
};

export const getRelativePosition = (x: number, relativeTo: number) => {
  return x / relativeTo;
};

export const isItemOutsideRelativeItemBounds = (
  itemPosition: { x: number; y: number },
  itemSize: { width: number; height: number },
  relativeItem: {
    position: { x: number; y: number };
    size: { width: number; height: number };
  }
): { isOutsideHorizontally: boolean; isOutsideVertically: boolean } => {
  const headerStartX = relativeItem.position.x;
  const headerEndX = relativeItem.position.x + relativeItem.size.width;
  const headerStartY = relativeItem.position.y;
  const headerEndY = relativeItem.position.y + relativeItem.size.height;

  const itemEndX = itemPosition.x + itemSize.width;
  const itemEndY = itemPosition.y + itemSize.height;

  // Check if item is outside horizontally or vertically
  const isOutsideHorizontally =
    itemPosition.x < headerStartX ||
    itemEndX > headerEndX ||
    itemPosition.x > headerEndX ||
    itemEndX < headerStartX;

  const isOutsideVertically =
    itemPosition.y < headerStartY ||
    itemEndY > headerEndY ||
    itemPosition.y > headerEndY ||
    itemEndY < headerStartY;

  return { isOutsideHorizontally, isOutsideVertically };
};

export const loadImageMetadata = async (
  imageUrl: string | undefined,
  previewWidth: number,
  previewHeight: number,
  heightRatio: number
): Promise<{
  relativeWidth: number;
  relativeHeight: number;
  relativeCenterX: number;
  relativeCenterY: number;
}> => {
  if (!imageUrl) {
    throw new Error("Image URL is required");
  }

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const height = previewHeight * heightRatio;
      const width = height * (img.width / img.height);

      const normalizedWidth = width / previewWidth;
      const normalizedHeight = height / previewHeight;

      const { width: scaledWidth, height: scaledHeight } =
        correctItemSizeToFitPreview(normalizedWidth, normalizedHeight);

      const relativeCenterX = (1 - scaledWidth) / 2;
      const relativeCenterY = (1 - scaledHeight) / 2;

      resolve({
        relativeWidth: scaledWidth,
        relativeHeight: scaledHeight,
        relativeCenterX,
        relativeCenterY,
      });
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
};

export const loadImageRatio = async (imageUrl: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve(img.width / img.height);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
};

export const loadVideoMetadata = async (
  videoUrl: string,
  previewWidth: number,
  previewHeight: number,
  heightRatio = DEFAULT_VIDEO_RELATIVE_HEIGHT
): Promise<{
  relativeWidth: number;
  relativeHeight: number;
  relativeCenterX: number;
  relativeCenterY: number;
}> => {
  if (!videoUrl) {
    throw new Error("Video URL is required");
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement("video");

    video.preload = "metadata";

    video.onloadedmetadata = () => {
      const height = previewHeight * heightRatio;
      const width = height * (video.videoWidth / video.videoHeight);

      const normalizedWidth = width / previewWidth;
      const normalizedHeight = height / previewHeight;

      const { width: scaledWidth, height: scaledHeight } =
        correctItemSizeToFitPreview(normalizedWidth, normalizedHeight);

      const relativeCenterX = (1 - scaledWidth) / 2;
      const relativeCenterY = (1 - scaledHeight) / 2;

      resolve({
        relativeWidth: scaledWidth,
        relativeHeight: scaledHeight,
        relativeCenterX,
        relativeCenterY,
      });
    };

    video.onerror = () => {
      reject(new Error("Failed to load video metadata"));
    };

    video.src = videoUrl;
  });
};

export const correctItemSizeToFitPreview = (
  relativeWidth: number,
  relativeHeight: number
) => {
  const aspectRatio = relativeWidth / relativeHeight;
  if (relativeWidth > 1) {
    return {
      width: 1,
      height: 1 / aspectRatio,
    };
  }
  if (relativeHeight > 1) {
    return {
      width: 1 * aspectRatio,
      height: 1,
    };
  }
  return {
    width: relativeWidth,
    height: relativeHeight,
  };
};

//BE formula to convert pixels to relative size and back
export const getRelativeFontSize = (fontSize: number, previewWidth: number) => {
  return Math.round((fontSize * 185) / previewWidth);
};
// BE int() truncates the result towards zero
export const getFontSizeInPixels = (fontSize: number, previewWidth: number) => {
  return Math.trunc((fontSize * previewWidth) / 185);
};

export const getStrokeInPixels = (
  stroke: Stroke | undefined,
  previewWidth: number
) => {
  if (!stroke) return undefined;
  return {
    ...stroke,
    width: stroke.width ? Math.round(stroke.width * previewWidth) : undefined,
  };
};

export const isVideo = (
  asset: Partial<MediaAsset> | BrandKitAsset
): boolean => {
  if (typeof asset !== "object") return false;
  return (
    ("asset_type" in asset && asset.asset_type === "Video") ||
    ("type" in asset && asset.type === "Video")
  );
};

export const calculateEstimatedGenerationTime = (
  sortedSections: StoryboardSection[]
) => {
  const scriptsWordsCount = sortedSections
    .map((x) => countScriptWords(x.script.content))
    .reduce((acc, count) => acc + count, 0);
  const readingTime = (60000 * scriptsWordsCount) / SPEECH_WORDS_PER_MINUTE; // seconds
  // round to 30 seconds
  return timePretty(Math.round((readingTime * 3) / 30000) * 30000);
};

export const addOpacityToHex = (hexColor: string, opacity: number) => {
  const cleanHex = hexColor.replace("#", "");
  if (opacity < 0 || opacity > 100) {
    throw new Error("Opacity must be between 0 and 100");
  }
  const alphaValue = Math.round((opacity / 100) * 255);

  const alphaHex = alphaValue.toString(16).padStart(2, "0");

  let baseColor = cleanHex;

  if (cleanHex.length === 8) {
    baseColor = cleanHex.substring(0, 6);
  } else if (cleanHex.length === 3) {
    baseColor = cleanHex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  return `#${baseColor}${alphaHex}`;
};

export const calculateIsDragged = (
  initialPosition: Position,
  draggablePosition: Position,
  previewWidth: number,
  previewHeight: number
) => {
  const currentX = getNumberRoundedToPrecision(
    draggablePosition.x / previewWidth
  );
  const currentY = getNumberRoundedToPrecision(
    draggablePosition.y / previewHeight
  );
  const originalX = getNumberRoundedToPrecision(initialPosition.x);
  const originalY = getNumberRoundedToPrecision(initialPosition.y);
  return currentX !== originalX || currentY !== originalY;
};

export const getPreviewSize = (
  isDesktop: boolean,
  containerHeight: number,
  containerWidth: number,
  aspectRatio: number
) => {
  if (isDesktop) {
    const width = Math.round(
      (containerWidth * SCENE_RELATIVE_WIDTH_DESKTOP) / 100
    );
    const height = Math.round(width / aspectRatio);
    return {
      width,
      height,
    };
  }

  const height = Math.round(
    (containerHeight * SCENE_RELATIVE_HEIGHT_MOBILE_TABLET) / 100
  );
  const width = Math.round(height * aspectRatio);
  return {
    width,
    height,
  };
};

export const calculateMediaAspectRatio = (mediaSettingsAspectRatio: number) => {
  if (mediaSettingsAspectRatio < TABLET_ASPECT_RATIO - 0.1) {
    return MOBILE_ASPECT_RATIO;
  }
  if (mediaSettingsAspectRatio < 1.5) {
    return TABLET_ASPECT_RATIO;
  }
  return DESKTOP_ASPECT_RATIO;
};

export const getPosition = (
  position: [string | number, string | number],
  itemWidth: number,
  itemHeight: number
) => {
  const x =
    typeof position[0] === "string"
      ? mapAlignment(position[0], itemWidth, "Header")
      : position[0];
  const y =
    typeof position[1] === "string"
      ? mapAlignment(position[1], itemHeight, "Header")
      : position[1];
  return {
    x,
    y,
  };
};

export const mapAlignment = (
  alignment: string,
  itemSize: number | null,
  itemType: string,
  referencedItemPosition = 0,
  referencedItemSize = 1
) => {
  if (!itemSize) {
    console.warn(`${itemType} size is not set, but has an alignment set.`);
    return 0;
  }
  switch (alignment) {
    case "left":
      return referencedItemPosition;
    case "right":
      return referencedItemPosition + referencedItemSize - itemSize;
    case "top":
      return referencedItemPosition;
    case "bottom":
      return referencedItemPosition + referencedItemSize - itemSize;
    case "center":
      return referencedItemPosition + (referencedItemSize - itemSize) / 2;
    case "outside_left":
      return referencedItemPosition - itemSize;
    case "outside_right":
      return referencedItemPosition + referencedItemSize + itemSize;
    case "outside_top":
      return referencedItemPosition - itemSize;
    case "outside_bottom":
      return referencedItemPosition + referencedItemSize + itemSize;
    case "none":
      console.warn(
        `${itemType} alignment is set to 'none', but has also no ratio defined.`
      );
      return 0;
    default:
      console.warn(
        `${itemType} alignment is set to a not recognized value: ${alignment}`
      );
      return 0;
  }
};

export const isItemSubsequencePresent = (
  itemsToCheck: TemplateItem[],
  templateItems: TemplateItem[]
) => {
  if (itemsToCheck.length === 0) return false;
  if (itemsToCheck.length > templateItems.length) return false;

  for (let i = 0; i <= templateItems.length - itemsToCheck.length; i++) {
    const slice = templateItems.slice(i, i + itemsToCheck.length);
    if (isEqual(slice, itemsToCheck)) {
      return true;
    }
  }
  return false;
};

export const getDraggableResizeHandleStyles = (
  position: ResizeHandlePosition
) => {
  const handleCorner = {
    width: 10,
    height: 10,
    boxShadow: "0 0 3px 0px #00000044",
    background: "white",
    borderRadius: 10,
  };

  const handleWidth = "24px";
  // Each handle wrapper uses translate(-50%, -50%), so 50% on a given axis
  // sits exactly on the edge. Nudge inward to keep handles inside the bounds.
  const insetFromTopLeft = "calc(50% + 1px)";
  const insetFromBottomRight = "calc(50% - 1px)";

  const handlerStyles = {
    top: {
      wrapper: {
        height: 4,
        width: handleWidth,
        top: insetFromTopLeft,
        left: "50%",
        borderRadius: "3px",
      },
      inner: { height: "30%", width: "70%" },
    },
    bottom: {
      wrapper: {
        height: "40%",
        width: handleWidth,
        top: insetFromBottomRight,
        left: "50%",
        borderRadius: "3px",
      },
      inner: { height: "30%", width: "70%" },
    },
    left: {
      wrapper: {
        height: handleWidth,
        width: "40%",
        top: "50%",
        left: insetFromTopLeft,
        borderRadius: "3px",
      },
      inner: { height: "70%", width: "30%" },
    },
    right: {
      wrapper: {
        height: handleWidth,
        width: "40%",
        top: "50%",
        left: insetFromBottomRight,
        borderRadius: "3px",
      },
      inner: { height: "70%", width: "30%" },
    },
    topLeft: {
      wrapper: {
        ...handleCorner,
        top: insetFromTopLeft,
        left: insetFromTopLeft,
      },
      inner: {},
    },
    topRight: {
      wrapper: {
        ...handleCorner,
        top: insetFromTopLeft,
        left: insetFromBottomRight,
      },
      inner: {},
    },
    bottomLeft: {
      wrapper: {
        ...handleCorner,
        top: insetFromBottomRight,
        left: insetFromTopLeft,
      },
      inner: {},
    },
    bottomRight: {
      wrapper: {
        ...handleCorner,
        top: insetFromBottomRight,
        left: insetFromBottomRight,
      },
      inner: {},
    },
  };

  return handlerStyles[position];
};

export const isCorner = (direction: ResizeHandlePosition) => {
  return ["topLeft", "topRight", "bottomLeft", "bottomRight"].includes(
    direction
  );
};

// Radius is calculated to the smaller dimension of the preview
export const getRelativeDimentionsToCalculateRadius = (
  previewWidth: number,
  previewHeight: number
) => {
  return Math.min(previewWidth, previewHeight);
};

export const generateStrokeStyles = (
  item: MediaItem,
  previewWidth: number,
  previewHeight: number
): CSSProperties => {
  const strokeWidth = Math.round((item.stroke?.width ?? 0) * previewWidth);
  const doubleStrokeWidth = strokeWidth * 2;
  const borderRadius = item.stroke?.radius
    ? item.stroke.radius *
      getRelativeDimentionsToCalculateRadius(previewWidth, previewHeight)
    : 0;
  return {
    border:
      item.stroke?.width && item.stroke.color
        ? `${strokeWidth}px solid ${item.stroke.color}`
        : "none",
    borderRadius,
    width: roundToHalfPixel(item.size.width * previewWidth) + doubleStrokeWidth,
    height:
      roundToHalfPixel(item.size.height * previewHeight) + doubleStrokeWidth,
  };
};

export const calculateNewItemSizeAndPosition = (
  height: number,
  positionX: number,
  newItemRatio: number,
  mediaAspectRatio: number
): { size: Size; positionX: number } => {
  let newHeight = height;
  let newWidth = (newHeight * newItemRatio) / mediaAspectRatio;
  let newPositionX = positionX;

  // If width alone exceeds preview, cap it and adjust height
  if (newWidth > 1) {
    newWidth = 1;
    newHeight = mediaAspectRatio / newItemRatio;
  }

  // If logo extends beyond preview edge, shift position to fit
  if (newPositionX + newWidth > 1) {
    newPositionX = 1 - newWidth;
  }

  return {
    size: { height: newHeight, width: newWidth },
    positionX: newPositionX,
  };
};

/**
 * Recompute an avatar's size and on-screen position for a new image aspect
 * ratio, preserving its current height, horizontal centering and bottom
 * anchoring. Shared by the single-avatar change (AvatarTab) and the
 * propagate-to-all-chapters flow so every avatar is resized identically.
 */
export const computeAvatarLayoutForRatio = (
  currentSize: Size,
  currentPosition: Position,
  imageRatio: number,
  mediaAspectRatio: number
): { size: Size; position: Position } => {
  const { size, positionX } = calculateNewItemSizeAndPosition(
    currentSize.height === 0 ? 0.4 : currentSize.height,
    currentPosition.x,
    imageRatio,
    mediaAspectRatio
  );
  const wasAnchoredToBottom =
    currentSize.height > 0 && currentPosition.y + currentSize.height >= 1;
  const newY = wasAnchoredToBottom
    ? Math.max(0, 1 - size.height)
    : Math.min(
        Math.max(currentPosition.y, 0),
        Math.max(0, 1 - size.height)
      );
  const wasHorizontallyCentered =
    currentSize.width > 0 &&
    Math.abs(currentPosition.x + currentSize.width / 2 - 0.5) < 1e-6;
  const newX = wasHorizontallyCentered ? (1 - size.width) / 2 : positionX;
  return { size, position: { x: newX, y: newY } };
};

// Keep an item's own bottom/center anchoring when only its size changes.
export const fitPositionToSize = (
  currentSize: Size,
  currentPosition: Position,
  newSize: Size
): Position => {
  const wasAnchoredToBottom =
    currentSize.height > 0 && currentPosition.y + currentSize.height >= 1;
  const wasHorizontallyCentered =
    currentSize.width > 0 &&
    Math.abs(currentPosition.x + currentSize.width / 2 - 0.5) < 1e-6;
  const maxX = Math.max(0, 1 - newSize.width);
  const maxY = Math.max(0, 1 - newSize.height);
  return {
    x: wasHorizontallyCentered
      ? (1 - newSize.width) / 2
      : Math.min(currentPosition.x, maxX),
    y: wasAnchoredToBottom
      ? maxY
      : Math.min(Math.max(currentPosition.y, 0), maxY),
  };
};

/**
 * Recompute a logo's size and horizontal position for a new image aspect
 * ratio while keeping its previous visual center, so replacing a logo with one
 * of a different width doesn't leave it off-center. The vertical position is
 * preserved as-is (only the width changes with the ratio). Shared by the
 * single-logo replace and the propagate-to-all-chapters flows in BrandTab.
 */
export const computeLogoLayoutForRatio = (
  currentSize: Size,
  currentPosition: Position,
  imageRatio: number,
  mediaAspectRatio: number
): { size: Size; position: Position } => {
  const { size } = calculateNewItemSizeAndPosition(
    currentSize.height,
    currentPosition.x,
    imageRatio,
    mediaAspectRatio
  );
  const centerX = currentPosition.x + currentSize.width / 2;
  const newX = Math.min(
    Math.max(centerX - size.width / 2, 0),
    Math.max(0, 1 - size.width)
  );
  return { size, position: { x: newX, y: currentPosition.y } };
};

export const adjustSizeToViewportIfNeeded = (
  size: Size,
  mediaAspectRatio: number
) => {
  let { width, height } = size;

  if (width > 1) {
    height = height / (width / mediaAspectRatio) / mediaAspectRatio;
    width = 1;
    return { width, height };
  }

  if (height > 1) {
    width = (width / (height * mediaAspectRatio)) * mediaAspectRatio;
    height = 1;
    return { width, height };
  }

  return size;
};

export const isTransparentHex = (hexColor: string) => {
  const cleanHex = hexColor.replace("#", "");
  if (cleanHex.length === 8) {
    const alpha = cleanHex.substring(6, 8);
    if (alpha === "00") {
      return true;
    }
  }
  return false;
};

/**
 * Measures the minimum content height for a text element at a given width.
 * Temporarily applies the new width and removes height constraints to measure
 * the natural text height with proper wrapping.
 */
export const measureTextMinHeight = (
  textElement: HTMLElement,
  newWidth: number
): number => {
  const originalHeight = textElement.style.height;
  const originalMinHeight = textElement.style.minHeight;
  const originalWidth = textElement.style.width;

  // Apply new width to measure text wrapping correctly
  textElement.style.width = `${newWidth}px`;
  textElement.style.height = "auto";
  textElement.style.minHeight = "auto";

  const minContentHeight = textElement.scrollHeight;

  // Restore original styles
  textElement.style.width = originalWidth;
  textElement.style.height = originalHeight;
  textElement.style.minHeight = originalMinHeight;

  return minContentHeight;
};

export const getBrandkitDataFromBrandkit = (
  brandKit: BrandKit
): ReturnType<typeof useBrandKit> => {
  const assets = brandKit.assets;
  return {
    audio: assets.find((asset) => asset.purpose === "Music"),
    backgroundBrandKitPicture: assets.find(
      (asset) => asset.purpose === "Background" && asset.type === "Picture"
    ),
    backgroundBrandKitVideo: assets.find(
      (asset) => asset.purpose === "Background" && asset.type === "Video"
    ),
    banner: assets.find((asset) => asset.purpose === "Banner"),
    brandKitName: brandKit.name,
    brandKitPalette: brandKit.palette,
    closing: assets.find((asset) => asset.purpose === "Closing"),
    intro: assets.find((asset) => asset.purpose === "Introduction"),
    logo: assets.find((asset) => asset.purpose === "Logo"),
    subtitlesFont: assets.find((asset) => asset.purpose === "Subtitles"),
    textFont: assets.find((asset) => asset.purpose === "Text"),
    titleFont: assets.find((asset) => asset.purpose === "Title"),
    transition: assets.find((asset) => asset.purpose === "Transition"),
  } as ReturnType<typeof useBrandKit>;
};

export const isDefaultPublicFontValid = (
  font: Partial<MediaAsset> | undefined
): font is MediaAsset => {
  return font?.id !== undefined;
};

// If the transition's title should be read aloud or an avatar is shown on it,
// speechType must be FixedScript instead of Silence. Returns the same
// reference when no change is needed.
export const withFixedScriptIfNeeded = (
  transition: VideoScene,
  shouldForce: boolean
): VideoScene =>
  shouldForce && transition.speechType !== "FixedScript"
    ? { ...transition, speechType: "FixedScript" }
    : transition;

export type LayerableCollectionItem =
  | MediaItem
  | Shape
  | CustomText
  | ParameteredText
  | MainImage
  | Table;

// The template item types that are always layerable regardless of chapter vs.
// transition view (unlike header/logo/presenter/textStyle/titleStyle, which
// are gated by view and visibility flags at each call site). Shared so
// `useChangeLayer.getSortedStoreItems` and `getHighestNonPresenterLayerIndex`
// (useTemplateEditorStore) can't drift when a new item type is added.
export const getLayerableCollectionItems = (
  state: Pick<
    TemplateState,
    | "images"
    | "videos"
    | "customTexts"
    | "shapes"
    | "parameteredTexts"
    | "figure"
    | "mainImage"
    | "slideLayoutShapes"
    | "table"
  >
): LayerableCollectionItem[] =>
  [
    ...state.images,
    ...state.videos,
    ...state.customTexts,
    ...(state.parameteredTexts ?? []),
    state.figure?.number,
    state.figure?.units,
    state.mainImage,
    ...(state.slideLayoutShapes ?? []),
    state.table,
    ...state.shapes,
  ].filter((item): item is LayerableCollectionItem => item !== undefined);
